import { useState } from 'react'
import { motion } from 'motion/react'
import { BarChart, Bar, XAxis, YAxis, Cell, ResponsiveContainer } from 'recharts'
import type { LiveHrSummaryOut } from '../../api/types'
import { Eyebrow, TOKENS } from '../ui/dashboard-primitives'

/** Bar tint per HR zone — mirrors the iOS History HR-zone chart so both
 *  platforms read the same. z1 coolest → z5 hottest. */
function zoneColor(zone: string): string {
  switch (zone.toLowerCase()) {
    case 'z1':
      return TOKENS.creamDim
    case 'z2':
      return TOKENS.amberGlow
    case 'z3':
      return TOKENS.amber
    case 'z4':
      return TOKENS.amberLight
    default:
      return TOKENS.terracotta
  }
}

interface HrZoneChartProps {
  live: LiveHrSummaryOut
}

/**
 * Heart-rate zone bar chart for the day's phone-driven live session — minutes
 * spent per zone, with avg/max HR and duration alongside. Rendered only when
 * the live session captured HR. Energy is deliberately NOT shown here — the
 * day's single energy figure lives in the top summary (DayDetailOut.energy_kcal);
 * a second number here was the same session's energy shown twice.
 */
export function HrZoneChart({ live }: HrZoneChartProps) {
  const [isVisible, setIsVisible] = useState(false)
  const zones = live.hr_zones ?? []

  const data = zones.map((z) => ({
    zone: z.zone.toUpperCase(),
    minutes: Math.round(z.minutes * 10) / 10,
    color: zoneColor(z.zone),
  }))

  const stats: Array<{ label: string; value: string }> = []
  if (live.duration_minutes != null)
    stats.push({ label: 'Duration', value: `${Math.round(live.duration_minutes)} min` })
  if (live.hr_avg != null) stats.push({ label: 'Avg HR', value: `${live.hr_avg} bpm` })
  if (live.hr_max != null) stats.push({ label: 'Max HR', value: `${live.hr_max} bpm` })

  // Nothing to show if the session captured neither zones nor summary stats.
  if (data.length === 0 && stats.length === 0) return null

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.6 }}
      onViewportEnter={() => setIsVisible(true)}
      className="rounded-2xl p-6"
      style={{
        background: TOKENS.creamHairline,
        border: '1px solid ' + TOKENS.creamBorder,
      }}
    >
      <div className="flex items-baseline justify-between mb-6">
        <Eyebrow>Heart Rate Zones</Eyebrow>
      </div>

      {stats.length > 0 && (
        <div className="flex flex-wrap gap-x-8 gap-y-3 mb-6">
          {stats.map((s) => (
            <div key={s.label} className="flex flex-col gap-1">
              <span
                className="tabular-nums"
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: '20px',
                  fontWeight: 600,
                  color: TOKENS.cream,
                }}
              >
                {s.value}
              </span>
              <span
                className="text-[9px] tracking-[0.14em] uppercase"
                style={{ fontFamily: 'var(--font-mono)', color: TOKENS.creamDim }}
              >
                {s.label}
              </span>
            </div>
          ))}
        </div>
      )}

      {data.length > 0 && (
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 4 }}>
            <XAxis
              dataKey="zone"
              stroke={TOKENS.creamFaint}
              // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
              tick={{ fill: 'rgba(245, 237, 225, 0.45)', fontSize: 10, fontFamily: 'var(--font-mono)' }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              stroke={TOKENS.creamFaint}
              // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
              tick={{ fill: 'rgba(245, 237, 225, 0.45)', fontSize: 10, fontFamily: 'var(--font-mono)' }}
              tickLine={false}
              axisLine={false}
              width={32}
              unit="m"
            />
            <Bar dataKey="minutes" radius={[3, 3, 0, 0]} isAnimationActive={isVisible} animationDuration={800}>
              {data.map((d) => (
                <Cell key={d.zone} fill={d.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </motion.div>
  )
}
